import { Link, useLocation } from 'react-router'
import { MessageSquare, GraduationCap, UsersRound, ShieldAlert, Settings2, ChartColumnBig, ChevronRight } from 'lucide-react'
import { cn } from '@neo-agents/ui'
import { SidebarFooter } from './SidebarFooter'
import { useWorkspace } from '../contexts/WorkspaceContext'
import { RobotLogo } from './RobotLogo'

interface NavItem {
  label: string
  href: string
  icon: React.ElementType
}

const NAV_ITEMS: NavItem[] = [
  { label: 'Usage', href: '/admin/usage', icon: ChartColumnBig },
  { label: 'My Classes', href: '/admin/my-classes', icon: Settings2 },
  { label: 'Flagged & Safety', href: '/admin/flagged', icon: ShieldAlert },
  { label: 'Students', href: '/admin/students', icon: GraduationCap },
  { label: 'Teachers', href: '/admin/teachers', icon: UsersRound },
]

export function AdminSidebar() {
  const { pathname } = useLocation()

  const isActive = (href: string) => {
    if (pathname === href) return true
    return pathname.startsWith(`${href}/`)
  }

  return (
    <aside className="flex h-full w-[248px] shrink-0 flex-col border-r-2 border-nb-border bg-sidebar">
      <div className="shrink-0 border-b-2 border-nb-border px-4 py-4">
        <div className="flex items-center gap-2">
          <RobotLogo className="h-7 w-7" />
          <span className="font-heading text-lg leading-none">Robolab X</span>
        </div>
        <p className="mt-2 text-sm text-foreground/60">Admin</p>
      </div>
      <nav className="flex-1 flex flex-col gap-1 p-3">
        {NAV_ITEMS.map((item) => (
          <Link
            key={item.href}
            to={item.href}
            className={cn(
              'flex items-center gap-2 px-3 py-2 rounded-base text-sm font-base transition-colors border-2',
              isActive(item.href)
                ? 'bg-main text-main-foreground border-nb-border nb-shadow'
                : 'border-transparent hover:bg-secondary-bg hover:border-nb-border',
            )}
          >
            <item.icon className="h-4 w-4 shrink-0" />
            {item.label}
          </Link>
        ))}
      </nav>
      <div className="shrink-0 px-3 pb-2">
        <Link
          to="/"
          className="flex items-center justify-between rounded-base border-2 border-nb-border bg-background px-3 py-3 nb-shadow transition-all hover:translate-x-boxShadowX hover:translate-y-boxShadowY hover:shadow-none"
        >
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex size-9 shrink-0 items-center justify-center rounded-base border-2 border-nb-border bg-main text-main-foreground">
              <MessageSquare className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <div className="font-heading text-sm leading-none text-foreground">Chat</div>
              <div className="mt-1 text-xs leading-tight text-foreground/55">Back to conversations</div>
            </div>
          </div>
          <ChevronRight className="h-4 w-4 shrink-0 text-foreground/35" />
        </Link>
      </div>
      <SidebarFooter />
    </aside>
  )
}
